import type { DbPortalGebietFeature, DbPortalSkalaEintrag, DbPortalZeitreiheEintrag } from '@gql/shared-types';
import type { Daten } from './laden.js';
import { sichtbareSkala } from './skala.js';
import { fassungZumStichtag, kategorieAusKonzessionaeren, stichtag, type Kategorie } from './zeitreihe.js';

export interface GebietImJahr {
  feature: DbPortalGebietFeature;
  fassung: DbPortalZeitreiheEintrag | null;
  kategorie: Kategorie;
  konzessionaerText: string;
  /** null = Skala ausgeblendet */
  skala: DbPortalSkalaEintrag | null;
}

export function firmenNamen(daten: Daten): Map<string, string> {
  return new Map(daten.konzessionaere.firmen.map((f) => [f.id, f.name]));
}

export function konzessionaerText(ids: readonly string[], namen: Map<string, string>): string {
  if (ids.length === 0) return 'kein Konzessionär erkannt';
  return ids.map((id) => namen.get(id) ?? id).join(', ');
}

export function gebieteImJahr(daten: Daten, jahr: number): GebietImJahr[] {
  const namen = firmenNamen(daten);
  const tag = stichtag(jahr);
  return daten.gebiete.features.map((feature) => {
    const fassung = fassungZumStichtag(feature.properties.zeitreihe, tag);
    return {
      feature,
      fassung,
      kategorie: fassung ? kategorieAusKonzessionaeren(fassung.k) : 'keine_fassung',
      konzessionaerText: fassung ? konzessionaerText(fassung.k, namen) : 'keine TAB-Fassung bekannt',
      skala: sichtbareSkala(daten.skala.gebiete[feature.properties.slug]),
    };
  });
}

export function skalaKurztext(s: DbPortalSkalaEintrag | null): string | null {
  if (!s) return null;
  if (s.referenz) return 'Referenz (Berlin)';
  if (s.status === 'offen' || s.wert === null) return 'Vergleichbarkeit noch offen';
  return `Vergleichbarkeit ${s.wert} von 5${s.status === 'vorläufig' ? ' (vorläufig)' : ''}`;
}

export const TYP_TEXT: Record<string, string> = {
  stadt: 'Stadt',
  landkreis: 'Landkreis',
  bundesland: 'Land',
};

/** Suchnormalisierung: Kleinschreibung, Umlaute und ß vereinheitlicht. */
export function normalisiere(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '');
}
